import { useSeo } from "../lib/seo";
import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, Clock } from "lucide-react";
import { submitContact } from "../lib/adventureApi";
import RALayout from "../components/ra/RALayout";

const DETAILS = [
  { icon: Phone, label: "Call us", value: "+977 9801082108" },
  { icon: Mail, label: "Email", value: "raj@responsibleadventures.com" },
  { icon: MapPin, label: "Office", value: "17 Sundarnagar, Amrit Marg, Kathmandu 44600, Nepal" },
  { icon: Clock, label: "Hours", value: "Sun–Fri, 9:00–18:00 NPT" },
];

export default function Contact() {
  useSeo({ title: "Contact us | Responsible Adventures", description: "Talk to Raj and the Responsible Adventures team in Kathmandu: +977 9801082108, raj@responsibleadventures.com." });
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in your name, email and message"); return; }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent", { description: "Thank you — our team will be in touch shortly." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full px-3.5 py-2.5 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta";

  return (
    <RALayout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-20">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-moss mb-4">Get in touch</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight leading-[0.98]" data-testid="contact-title">
          Let's plan your Himalayan journey.
        </h1>
        <p className="mt-5 text-lg text-ink-secondary max-w-2xl leading-relaxed">
          Tell us your dream — the mountain you've always pictured, the pace that suits you, the people you're
          travelling with — and our Kathmandu team will craft a journey around you. Travellers and trade partners
          are equally welcome.
        </p>

        <div className="mt-12 grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-3 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-moss">Name *</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} className={`mt-1 ${inputCls}`} data-testid="contact-name" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-moss">Email *</label>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`mt-1 ${inputCls}`} data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-moss">Subject</label>
              <input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="e.g. Everest Base Camp in October" className={`mt-1 ${inputCls}`} data-testid="contact-subject" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-moss">Message *</label>
              <textarea rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className={`mt-1 ${inputCls} resize-none`} data-testid="contact-message" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit"
              className="flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-6 h-12 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send message</>}
            </button>
          </form>

          {/* Details */}
          <div className="lg:col-span-2 space-y-4">
            {DETAILS.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.label} className="flex items-start gap-3 p-5 rounded-md border border-sand-dark bg-white">
                  <div className="w-10 h-10 rounded-sm bg-moss-light flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-moss" /></div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{d.label}</p>
                    <p className="text-sm text-ink font-medium mt-0.5 leading-snug">{d.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </RALayout>
  );
}
